import { useEffect, useState } from 'react';
import { useGeneralSettings } from './useQueries';

// Mở rộng Window interface để TypeScript nhận diện window.dataLayer
declare global {
  interface Window {
    dataLayer: Record<string, any>[];
  }
}

// ID định danh duy nhất cho thẻ script GTM trong DOM để chống chèn trùng lặp
export const GTM_SCRIPT_TAG_ID = 'gtm-dynamic-script';

/**
 * Helper function để push dữ liệu và sự kiện vào Google Tag Manager dataLayer.
 * An toàn khi gọi trong môi trường SSR/Client và tự động khởi tạo mảng nếu chưa tồn tại.
 *
 * @param event Tên sự kiện (ví dụ: 'login_success', 'spend_coin', 'page_view')
 * @param data Dữ liệu đi kèm sự kiện (ví dụ: { coin: 50, packageId: 'XU_100' })
 */
export const pushToDataLayer = (
  event: string,
  data?: Record<string, any>
): void => {
  if (typeof window === 'undefined') return;

  // Tự động khởi tạo dataLayer nếu chưa có
  window.dataLayer = window.dataLayer || [];

  // Push sự kiện kèm payload dữ liệu
  window.dataLayer.push({
    event,
    ...(data || {}),
  });
};

export interface UseDynamicGTMResult {
  /** GTM ID hiện tại (ví dụ: 'GTM-XXXXXX' hoặc null) */
  gtmId: string | null;
  /** Trạng thái thẻ script đã được chèn vào DOM hay chưa */
  isLoaded: boolean;
  /** Trạng thái đang tải cấu hình general_settings từ API */
  isLoading: boolean;
  /** Hàm helper để bắn sự kiện vào dataLayer */
  pushToDataLayer: typeof pushToDataLayer;
}

/**
 * Custom Hook: useDynamicGTM
 *
 * Tự động lấy cấu hình từ API GET /api/home_configs/general_settings,
 * trích xuất `googleAnalyticsId` (định dạng GTM-XXXXXX),
 * khởi tạo window.dataLayer và chèn thẻ script Google Tag Manager vào <head>.
 *
 * @param explicitGtmId (Tùy chọn) GTM ID truyền trực tiếp nếu không muốn đọc từ API
 */
export const useDynamicGTM = (explicitGtmId?: string): UseDynamicGTMResult => {
  const { data: settingsResponse, isLoading } = useGeneralSettings();
  const [isLoaded, setIsLoaded] = useState<boolean>(false);

  // Trích xuất gtm_id từ nhiều vị trí phản hồi của backend để đảm bảo tính tương thích cao nhất
  const gtmId = (
    explicitGtmId ||
    settingsResponse?.googleAnalyticsId ||
    settingsResponse?.data?.googleAnalyticsId ||
    settingsResponse?.config?.googleAnalyticsId ||
    settingsResponse?.data?.gtm_id ||
    ''
  ).trim();

  useEffect(() => {
    // 1. Nếu không có gtmId hợp lệ hoặc đang chạy phía Server thì bỏ qua
    if (!gtmId || typeof document === 'undefined') {
      return;
    }

    // 2. KHỞI TẠO DATALAYER: Tự động khởi tạo mảng window.dataLayer nếu chưa có
    window.dataLayer = window.dataLayer || [];

    // 3. CƠ CHẾ KIỂM TRA TRÙNG LẶP (Duplication Guard)
    // Kiểm tra xem thẻ script có ID định danh đã tồn tại trong DOM chưa (do re-render hoặc đã chèn trước đó)
    const existingScript = document.getElementById(GTM_SCRIPT_TAG_ID);
    if (existingScript) {
      // Nếu đã có và đúng ID thì đánh dấu là loaded và không chèn lại
      if (existingScript.getAttribute('data-gtm-id') === gtmId) {
        setIsLoaded(true);
        return;
      }
      // Nếu gtmId thay đổi (ví dụ Admin đổi GTM ID mới), xóa thẻ cũ để nạp thẻ mới
      existingScript.remove();
    }

    // 4. SINH ĐOẠN SCRIPT CHUẨN GOOGLE TAG MANAGER
    // Sử dụng template literal chứa gtmId theo chuẩn chính thức của Google
    const script = document.createElement('script');
    script.id = GTM_SCRIPT_TAG_ID;
    script.setAttribute('data-gtm-id', gtmId);
    script.async = true;
    script.innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','${gtmId}');`;

    // 5. INJECT VÀO THẺ <head> CỦA DOCUMENT
    document.head.appendChild(script);
    setIsLoaded(true);

  }, [gtmId]);

  return {
    gtmId: gtmId || null,
    isLoaded,
    isLoading,
    pushToDataLayer,
  };
};

export default useDynamicGTM;
